import { zodResolver } from '@hookform/resolvers/zod';
import { Label } from '@radix-ui/react-label';
import { updatePassword } from 'aws-amplify/auth';
import { LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { changePasswordSchema } from 'schemas/changePasswordSchema';
import { toast } from 'sonner';
import z from 'zod';
import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import { getPasswordCriteria, PasswordCriteriaList } from '../ui/PasswordCriteriaList';

type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>;

export const ChangePasswordForm = ({ onBack }: { onBack: () => void }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onChange',
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const newPasswordValue = watch('newPassword');
  const confirmPasswordValue = watch('confirmPassword');

  const allCriteriaMet = Object.values(getPasswordCriteria(newPasswordValue)).every(Boolean);
  const passwordsMatch =
    newPasswordValue === confirmPasswordValue && confirmPasswordValue.length > 0;

  const handleSubmitForm = async ({ currentPassword, newPassword }: ChangePasswordFormValues) => {
    setLoading(true);
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      toast.success(t('toast.passwordChangedSuccess'));
      reset();
      onBack();
    } catch (error) {
      const err = error as { message?: string };
      toast.error(err.message || t('toast.failedChangePassword'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit(handleSubmitForm)}>
      <div className="space-y-2">
        <Label htmlFor="currentPassword">{t('changePasswordForm.currentPassword')}</Label>
        <Input
          id="currentPassword"
          isPassword
          placeholder={t('changePasswordForm.currentPasswordPlaceholder')}
          disabled={loading}
          {...register('currentPassword')}
        />
        {errors.currentPassword && (
          <p className="text-xs text-red-500">{t(errors.currentPassword.message ?? '')}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="up-newPassword">{t('changePasswordForm.newPassword')}</Label>
        <Input
          id="up-newPassword"
          isPassword
          placeholder={t('changePasswordForm.newPasswordPlaceholder')}
          disabled={loading}
          {...register('newPassword')}
        />
        {errors.newPassword && (
          <p className="text-xs text-red-500">{t(errors.newPassword.message ?? '')}</p>
        )}
      </div>

      <PasswordCriteriaList password={newPasswordValue} />

      <div className="space-y-2">
        <Label htmlFor="up-confirmPassword">{t('changePasswordForm.confirmNewPassword')}</Label>
        <Input
          id="up-confirmPassword"
          isPassword
          placeholder={t('changePasswordForm.confirmPasswordPlaceholder')}
          disabled={loading}
          {...register('confirmPassword')}
        />
        {errors.confirmPassword && (
          <p className="text-xs text-red-500">{t(errors.confirmPassword.message ?? '')}</p>
        )}
      </div>

      <div className="space-y-3 pt-1">
        <Button
          type="submit"
          className="h-12 w-full rounded-2xl text-base font-semibold"
          disabled={!allCriteriaMet || !passwordsMatch || loading}
        >
          {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
          {loading ? t('changePasswordForm.updating') : t('changePasswordForm.changePassword')}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="h-12 w-full rounded-2xl text-base"
          disabled={loading}
          onClick={onBack}
        >
          {t('changePasswordForm.back')}
        </Button>
      </div>
    </form>
  );
};
